import { combineEpics } from 'redux-observable';
import { forkJoin, of } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/mapTo';
import { mergeMap, switchMap, tap } from 'rxjs/operators';

import { dictionariesRequest, dictionaryIds } from '@dict';
import { typeSel } from '@location';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';
import {
  DELETE_PROFILE_BIO_REQUEST,
  SAVE_PROFILE_BIO_REQUEST,
} from '@shared/duck';
import { SET_ORGANIZATION } from '@shared/duck/actions';
import { intl } from '@shared/localization';
import { toastService } from '@toasts';
import { unitInfoRequest } from '@unit/duck/actions';
import { catchAndReport } from '@utils/rxjs/operators';

import {
  ROUTE_LOGIN,
  USER_DATA_ERROR,
  USER_DATA_RESPONSE,
  personGuidSel,
  userDataRequest,
  userIdSel,
} from '../../user';
import {
  ROUTE_PROFILE,
  SAVE_AKELA_PROFILE_REQUEST,
  SAVE_PROFILE_REQUEST,
  SET_EDIT_PROFILE,
  saveAkelaProfileError,
  saveAkelaProfileResponse,
  saveProfileError,
  saveProfileResponse,
} from './actions';
import services from './services';

const fetchFormDicts$ = action$ =>
  action$
    .ofType(SET_EDIT_PROFILE)
    .filter(({ payload }) => payload)
    .mapTo(
      dictionariesRequest(dictionaryIds.STATES, dictionaryIds.PHONE_COUNTRIES),
    );

const saveProfile$ = (action$, state$) =>
  action$.ofType(SAVE_PROFILE_REQUEST).switchMap(({ payload }) => {
    const state = state$.value;
    const personGuid = personGuidSel(state);
    const userId = userIdSel(state);
    const { imageFile } = payload;
    const imgType = imageFile.type;

    if (
      payload.dateJoinedBoyScouts ||
      payload.dateJoinedVenturing ||
      payload.dateJoinedSeaScouts ||
      payload.dateJoinedBoyScouts == '' ||
      payload.dateJoinedVenturing == '' ||
      payload.dateJoinedSeaScouts == ''
    ) {
      return services
        .saveProfileJoinedDate$({ payload, userId })
        .mapTo(userDataRequest())
        .catchAndReport(err => Observable.of(saveProfileError(err)));
    } else {
      // SBL-3868

      return forkJoin([
        services.saveProfilePicture$({ ...payload, personGuid }),
        services.getPreSignedUrl$(userId, imgType).pipe(
          switchMap(response =>
            services.imageUploadS3$(userId, imgType, payload, response),
          ),
          tap(() =>
            toastService.info(
              intl.formatMessage({
                id: 'profile.ProfileInfo.save.profileSaveDisclaimer',
              }),
            ),
          ),
          catchAndReport(err => Observable.of(saveProfileError(err))),
        ),
      ]).pipe(
        mergeMap(() => Observable.concat(of(userDataRequest()))),
        catchAndReport(err => Observable.of(saveProfileError(err))),
      );
    }
  });

const saveProfileBio$ = action$ =>
  action$.ofType(SAVE_PROFILE_BIO_REQUEST).switchMap(({ payload }) => {
    const { profile, personGuid } = payload;

    return services
      .saveProfile$({ personGuid, profile })
      .mapTo(userDataRequest())
      .pipe(
        tap(() =>
          toastService.success(
            intl.formatMessage({
              id: 'shared.Bio.update.success',
            }),
          ),
        ),
      )
      .catchAndReport(err => Observable.of(saveProfileError(err)));
  });

const deleteProfileBio$ = action$ =>
  action$.ofType(DELETE_PROFILE_BIO_REQUEST).switchMap(({ payload }) => {
    const { bio, personGuid } = payload;
    return services
      .deleteProfileItem$(bio, personGuid)
      .mapTo(userDataRequest())
      .pipe(
        tap(() =>
          toastService.success(
            intl.formatMessage({
              id: 'shared.Bio.delete.success',
            }),
          ),
        ),
        catchAndReport(err => Observable.of(saveProfileError(err))),
      );
  });

const saveProfileSibscriptions = (subs = [], personGuid) => {
  if (!subs.length) {
    return [of({})];
  }

  return subs.map(sub =>
    sub.id
      ? services.updateUsersubscriptions$(sub, personGuid)
      : services.createUsersubscriptions$(sub, personGuid),
  );
};

const saveAkelaProfile$ = (action$, state$) =>
  action$.ofType(SAVE_AKELA_PROFILE_REQUEST).switchMap(({ payload }) => {
    const state = state$.value;
    const personGuid = personGuidSel(state);
    const userId = userIdSel(state);
    const { akelaProfile, sbProfile } = payload;

    return forkJoin([
      services.updateSBProfile$(sbProfile, userId),
      services.updateProfile$(akelaProfile, personGuid),
      ...saveProfileSibscriptions(akelaProfile.subs, personGuid),
    ]).pipe(
      tap(() =>
        toastService.success(
          intl.formatMessage({
            id: 'profile.ProfileInfo.save.success',
          }),
        ),
      ),
      mergeMap(() =>
        Observable.concat(
          of(
            esbApi.util.invalidateTags([
              { type: tagTypes.CompletePersonProfile, id: personGuid },
              ...(akelaProfile?.subs?.length
                ? [{ type: tagTypes.PersonSubscriptions, id: personGuid }]
                : []),
            ]),
          ),
          of(saveAkelaProfileResponse()),
          of(userDataRequest()),
        ),
      ),
      catchAndReport(err => Observable.of(saveAkelaProfileError(err))),
    );
  });

const loadProfileResponseEpic$ = action$ =>
  action$.ofType(SAVE_PROFILE_REQUEST).switchMap(() =>
    action$
      .ofType(USER_DATA_RESPONSE)
      .take(1)
      .mapTo(saveProfileResponse())
      .takeUntil(action$.ofType(USER_DATA_ERROR))
      .do(() =>
        toastService.success(
          intl.formatMessage({
            id: 'profile.ProfileInfo.save.success',
          }),
        ),
      ),
  );

const refetchUnitInfo$ = (action$, state$) =>
  action$
    .ofType(SET_ORGANIZATION)
    .filter(() => [ROUTE_PROFILE, ROUTE_LOGIN].includes(typeSel(state$.value)))
    .mapTo(unitInfoRequest());

export default combineEpics(
  fetchFormDicts$,
  saveProfile$,
  saveAkelaProfile$,
  saveProfileBio$,
  deleteProfileBio$,
  loadProfileResponseEpic$,
  refetchUnitInfo$,
);
